// packages/web-ui/src/components/file-tree/FileTree.tsx
//
// Story W3-S5 AC #2 — virtualised file-tree row renderer.
//
// Implementation notes:
// - Below `VIRTUALISE_THRESHOLD` (300) rows, render plain `<div>`s. Above the
//   threshold the consumer SHOULD swap in a virtualiser (`@tanstack/react-virtual`
//   is already in the project — see W2-S5). For this story we expose a single
//   stable component; callers wishing virtualisation pass `virtualWindowSize`
//   to enable a simple windowing fallback.
// - Per-row glyph priority: `✗` (validation error) > `●` (dirty) > `⊕` (newly created).
//   The consumer supplies the boolean state for each row via `getRowFlags`.
// - Keyboard nav: ArrowUp/Down move selection within visible rows; Right
//   expands a collapsed dir or moves to first child of an expanded dir; Left
//   collapses an expanded dir or moves to its parent; Enter activates the row
//   via `onOpen`.
// - Drag emits both `application/x-tessa-lean-path` (preferred, picked up by
//   the chat composer drop handler) and `text/plain` fallback (for external
//   drop targets — VS Code, terminals, etc.).

import type { JSX, KeyboardEvent, MouseEvent } from 'react';
import { useCallback, useMemo } from 'react';
import type { FileTreeNode } from '../../api/clients/files.js';
import type { FlatTreeRow } from './flatten.js';
import { splitForHighlight } from './filter.js';

export const VIRTUALISE_THRESHOLD = 300;

export interface RowFlags {
  /** File has ≥ 1 validation error (server-aggregated or client-fresher). */
  hasErrors?: boolean;
  /** File has unsaved local changes. */
  isDirty?: boolean;
  /** File is part of the in-flight changeset's `pendingCreates`. */
  isNewlyCreated?: boolean;
  /** Row is currently being renamed — server response pending. */
  isRenamePending?: boolean;
  /** Row is being dragged — fades to 50 % opacity. */
  isDragging?: boolean;
  /** Row is currently pulsing from a `files:changed` event. */
  isPulsing?: boolean;
}

export interface FileTreeProps {
  /** Already-flattened + filtered visible rows. */
  rows: readonly FlatTreeRow[];
  /** Currently keyboard-focused row path (`null` = first row). */
  selectedPath: string | null;
  /** Selection-update callback (keyboard nav). */
  onSelectionChange?: (path: string | null) => void;
  /** Fires on row click + Enter / Space keypress. */
  onOpen?: (path: string) => void;
  /** Fires on chevron click — only invoked for dir rows. */
  onToggleExpand?: (path: string) => void;
  /** Right-click handler — receives the node and the click coordinates. */
  onContextMenu?: (node: FileTreeNode, x: number, y: number) => void;
  /** Drag-start payload. Default emits both MIME types. */
  onDragStart?: (event: React.DragEvent, node: FileTreeNode) => void;
  /** Per-row flag accessor. Default returns `{}` for every row. */
  getRowFlags?: (node: FileTreeNode) => RowFlags;
  /** Active search query — drives highlighting via `splitForHighlight`. */
  query?: string;
  /**
   * Optional windowing override. When `rows.length > VIRTUALISE_THRESHOLD`
   * AND `virtualWindowSize` is set, only the slice
   * `[firstVisibleIndex, firstVisibleIndex + virtualWindowSize]` is rendered.
   * Tests pass `firstVisibleIndex` to control which window is in view.
   */
  virtualWindowSize?: number;
  /** First visible row when virtualised — only consulted with `virtualWindowSize`. */
  firstVisibleIndex?: number;
}

const NEWLY_CREATED_GLYPH = '⊕'; // ⊕
const DIRTY_GLYPH = '●'; // ●
const ERROR_GLYPH = '✗'; // ✗

function priorityGlyph(flags: RowFlags): string | null {
  if (flags.hasErrors === true) return ERROR_GLYPH;
  if (flags.isDirty === true) return DIRTY_GLYPH;
  if (flags.isNewlyCreated === true) return NEWLY_CREATED_GLYPH;
  return null;
}

function defaultDragStart(event: React.DragEvent, node: FileTreeNode): void {
  event.dataTransfer.setData('application/x-tessa-lean-path', node.path);
  event.dataTransfer.setData('text/plain', node.path);
  event.dataTransfer.effectAllowed = 'copyLink';
}

export function FileTree(props: FileTreeProps): JSX.Element {
  const {
    rows,
    selectedPath,
    onSelectionChange,
    onOpen,
    onToggleExpand,
    onContextMenu,
    onDragStart,
    getRowFlags,
    query,
    virtualWindowSize,
    firstVisibleIndex,
  } = props;

  const visibleRows = useMemo(() => {
    if (
      rows.length > VIRTUALISE_THRESHOLD
      && typeof virtualWindowSize === 'number'
      && virtualWindowSize > 0
    ) {
      const start = Math.max(0, firstVisibleIndex ?? 0);
      return rows.slice(start, start + virtualWindowSize);
    }
    return rows;
  }, [rows, virtualWindowSize, firstVisibleIndex]);

  const visibleStartIndex = useMemo(() => {
    if (
      rows.length > VIRTUALISE_THRESHOLD
      && typeof virtualWindowSize === 'number'
      && virtualWindowSize > 0
    ) {
      return Math.max(0, firstVisibleIndex ?? 0);
    }
    return 0;
  }, [rows, virtualWindowSize, firstVisibleIndex]);

  const moveSelection = useCallback(
    (delta: number) => {
      if (rows.length === 0) return;
      const idx = rows.findIndex((r) => r.node.path === selectedPath);
      const nextIdx = idx === -1 ? 0 : Math.min(rows.length - 1, Math.max(0, idx + delta));
      onSelectionChange?.(rows[nextIdx]?.node.path ?? null);
    },
    [rows, selectedPath, onSelectionChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, row: FlatTreeRow) => {
      const { node, isExpanded } = row;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveSelection(+1);
          return;
        case 'ArrowUp':
          event.preventDefault();
          moveSelection(-1);
          return;
        case 'ArrowRight':
          if (node.kind === 'directory' && isExpanded === false) {
            event.preventDefault();
            onToggleExpand?.(node.path);
          } else if (node.kind === 'directory' && isExpanded === true) {
            event.preventDefault();
            moveSelection(+1);
          }
          return;
        case 'ArrowLeft':
          if (node.kind === 'directory' && isExpanded === true) {
            event.preventDefault();
            onToggleExpand?.(node.path);
          } else if (typeof row.parentPath === 'string') {
            event.preventDefault();
            onSelectionChange?.(row.parentPath);
          }
          return;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (node.kind === 'directory') onToggleExpand?.(node.path);
          else onOpen?.(node.path);
          return;
        default:
          return;
      }
    },
    [moveSelection, onToggleExpand, onOpen, onSelectionChange],
  );

  const handleClick = useCallback(
    (row: FlatTreeRow) => {
      onSelectionChange?.(row.node.path);
      if (row.node.kind === 'directory') onToggleExpand?.(row.node.path);
      else onOpen?.(row.node.path);
    },
    [onSelectionChange, onToggleExpand, onOpen],
  );

  const handleContextMenu = useCallback(
    (event: MouseEvent, row: FlatTreeRow) => {
      event.preventDefault();
      onContextMenu?.(row.node, event.clientX, event.clientY);
    },
    [onContextMenu],
  );

  return (
    <div role="tree" data-testid="file-tree" data-virtualised={rows.length > VIRTUALISE_THRESHOLD}>
      {visibleRows.map((row, i) => {
        const flags = getRowFlags?.(row.node) ?? {};
        const glyph = priorityGlyph(flags);
        const isSelected = row.node.path === selectedPath;
        const segments = splitForHighlight(row.node.name, query ?? '');
        const indent = row.depth * 16;
        const rowAriaLevel = row.depth + 1;
        return (
          <div
            key={row.node.path}
            role="treeitem"
            tabIndex={isSelected ? 0 : -1}
            aria-level={rowAriaLevel}
            aria-selected={isSelected}
            aria-expanded={row.node.kind === 'directory' ? row.isExpanded ?? false : undefined}
            data-testid={`file-tree-row-${row.node.path}`}
            data-path={row.node.path}
            data-kind={row.node.kind}
            data-depth={row.depth}
            data-index={visibleStartIndex + i}
            data-selected={isSelected ? 'true' : 'false'}
            data-pulse={flags.isPulsing === true ? 'true' : undefined}
            data-dragging={flags.isDragging === true ? 'true' : undefined}
            data-rename-pending={flags.isRenamePending === true ? 'true' : undefined}
            className="file-tree-row flex cursor-pointer select-none items-center"
            style={{ paddingLeft: `${indent}px` }}
            draggable={row.node.kind === 'file'}
            onClick={() => handleClick(row)}
            onContextMenu={(e) => handleContextMenu(e, row)}
            onKeyDown={(e) => handleKeyDown(e, row)}
            onDragStart={(e) => {
              (onDragStart ?? defaultDragStart)(e, row.node);
            }}
          >
            <span
              data-testid={`file-tree-chevron-${row.node.path}`}
              aria-hidden="true"
              className="inline-block w-4 text-center text-[10px]"
              onClick={(e) => {
                if (row.node.kind !== 'directory') return;
                e.stopPropagation();
                onToggleExpand?.(row.node.path);
              }}
            >
              {row.node.kind === 'directory' ? (row.isExpanded ? '▾' : '▸') : ''}
            </span>
            {glyph !== null ? (
              <span
                data-testid={`file-tree-glyph-${row.node.path}`}
                aria-hidden="true"
                className="inline-block w-4 text-center"
              >
                {glyph}
              </span>
            ) : (
              <span aria-hidden="true" className="inline-block w-4" />
            )}
            <span data-testid={`file-tree-name-${row.node.path}`}>
              {segments.map((seg, segIdx) =>
                seg.match ? (
                  <span
                    key={segIdx}
                    className="rounded-none bg-[var(--accent-cream-active,#E5E5E5)]"
                    data-match="true"
                  >
                    {seg.text}
                  </span>
                ) : (
                  <span key={segIdx}>{seg.text}</span>
                ),
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
